import React, { useContext } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import type { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { Category } from '@graphql/generated-base';
import { useScaleGuestSiteFontSize } from '@shared/core/website/theming/useScaleGuestSiteFontSize';
import { AnalyticsContext, useTranslation } from '@shared/core';
import type { ApolloError } from '@apollo/client';

type CustomComponentErrorStateProps = Readonly<{
  error: ApolloError;
  eventHandle: string;
}>;

const typographyOverride: OverrideTypography = [
  { variant: 'hed1', category: Category.HEADING },
  { variant: 'body2', category: Category.PARAGRAPH },
];

export const CustomComponentErrorState: React.FC<CustomComponentErrorStateProps> = ({ error, eventHandle }) => {
  const { scaleFontSize } = useScaleGuestSiteFontSize();
  const { tq } = useTranslation('guestSiteCustom');
  const { anonymousId } = useContext(AnalyticsContext);

  const errorCode =
    error.networkError && 'statusCode' in error.networkError
      ? `${error.networkError.statusCode}`
      : error.graphQLErrors?.[0]?.extensions?.code
        ? `${error.graphQLErrors[0].extensions.code}`
        : 'UNKNOWN';

  const diagnosticCode = [errorCode, eventHandle, anonymousId?.slice(0, 8)].filter(Boolean).join(' | ');

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      paddingX={6}
      paddingTop={pxToRem(44)}
      paddingBottom={pxToRem(44)}
      marginTop={8}
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        paddingY={pxToRem(44)}
        rowGap={pxToRem(44)}
        maxWidth={pxToRem(482)}
        marginX="auto"
      >
        <GuestSiteTypographyOverride override={typographyOverride}>
          <Flex
            flexDirection="column"
            rowGap={3}
            width="100%"
          >
            <TextV2
              typographyVariant="hed1"
              fontSize={scaleFontSize(pxToRem(26), Category.HEADING)}
              fontWeight={600}
              textAlign="center"
            >
              {tq`Unable to Load Content`}
            </TextV2>
            <TextV2
              typographyVariant="body2"
              maxWidth={pxToRem(314)}
              lineHeight={pxToRem(24)}
              textAlign="center"
            >
              {tq`Please try again later.`}
            </TextV2>
          </Flex>
        </GuestSiteTypographyOverride>
        <TextV2
          typographyVariant="label2"
          fontSize={pxToRem(10)}
          color="mono8"
          textAlign="center"
        >
          {tq`Error code: ${diagnosticCode}`}
        </TextV2>
      </Flex>
    </Flex>
  );
};
